import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError, imgUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import MapView from '@/components/MapView';
import { PUBLISH } from '@/constants/testIds';
import { ArrowRight, ArrowLeft, Check, Upload, X, Crosshair } from 'lucide-react';
import { track } from '@/lib/analytics';
import PublishSuccessModal from '@/components/PublishSuccessModal';

const STEPS = ['Tipo', 'Info', 'Localizzazione', 'Caratteristiche', 'Foto', 'Riepilogo'];
const CROPS = ['Vite', 'Olivo', 'Grano', 'Mais', 'Ortaggi', 'Frutteto', 'Foraggio', 'Nocciolo'];
const FEATS = [
  ['water','Acqua'], ['irrigation','Irrigazione'], ['electricity','Elettricità'],
  ['road_access','Strada di accesso'], ['buildings','Edifici'], ['fenced','Recinzioni'],
];

export default function PublishListing() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    listing_type: 'sale',
    title: '', description: '',
    price: '', rental_period: 'yearly', area_hectares: '', availability: 'available',
    land_type: 'seminativo',
    location: { region: '', province: '', comune: '', address: '', lat: 42.5, lng: 12.5 },
    features: {},
    crops: [],
    images: [],
    contact_phone: '',
    contact_whatsapp: '',
  });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // { listing_id, title }
  const [aiBusy, setAiBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geoResults, setGeoResults] = useState([]);
  const [addressQuery, setAddressQuery] = useState('');

  const setF = (k, v) => setForm({ ...form, [k]: v });
  const setLoc = (k, v) => setForm({ ...form, location: { ...form.location, [k]: v } });
  const toggleFeat = (k) => setForm({ ...form, features: { ...form.features, [k]: !form.features[k] } });
  const toggleCrop = (c) => setForm({ ...form, crops: form.crops.includes(c) ? form.crops.filter(x=>x!==c) : [...form.crops, c] });

  const upload = async (files) => {
    const arr = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const { data } = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        arr.push(data.file_id);
      } catch (e) { toast.error(formatApiError(e)); }
    }
    setForm(f => ({ ...f, images: [...f.images, ...arr] }));
  };

  const next = () => setStep(s => Math.min(STEPS.length-1, s+1));
  const prev = () => setStep(s => Math.max(0, s-1));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: form.price === '' ? null : Number(form.price),
        rental_period: form.listing_type === 'rent' ? form.rental_period : null,
        area_hectares: Number(form.area_hectares),
        location: { ...form.location, lat: Number(form.location.lat), lng: Number(form.location.lng) },
      };
      const { data } = await api.post('/listings', payload);
      track('publish_listing', { listing_type: form.listing_type, area: Number(form.area_hectares) });
      setSuccess({ listing_id: data.listing_id, title: form.title });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const runGeocode = async () => {
    const q = addressQuery.trim() ||
      [form.location.address, form.location.comune, form.location.province, form.location.region, 'Italia']
        .filter(Boolean).join(', ');
    if (q.length < 3) { toast('Inserisci un indirizzo, un comune o un CAP'); return; }
    setGeocoding(true); setGeoResults([]);
    try {
      const { data } = await api.post('/geocode', { query: q });
      if (!data.results?.length) { toast('Nessuna posizione trovata. Prova a essere più specifico.'); return; }
      setGeoResults(data.results);
      // Auto-apply best result
      const best = data.results[0];
      applyGeoResult(best);
      toast.success('Posizione trovata sulla mappa');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeocoding(false); }
  };

  const applyGeoResult = (r) => {
    setForm(f => ({
      ...f,
      location: {
        ...f.location,
        lat: r.lat,
        lng: r.lng,
        address: r.address || f.location.address,
        comune: r.comune || f.location.comune,
        province: r.province || f.location.province,
        region: r.region || f.location.region,
      }
    }));
  };

  const [geoLocating, setGeoLocating] = useState(false);
  const useCurrentPosition = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Il tuo browser non supporta la geolocalizzazione');
      return;
    }
    setGeoLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const { data } = await api.post('/geocode/reverse', { lat, lng });
          if (data?.result) {
            applyGeoResult(data.result);
            toast.success('Posizione attuale rilevata');
          } else {
            // Fallback: imposta solo lat/lng, lascia i campi testo da compilare
            setForm(f => ({ ...f, location: { ...f.location, lat, lng } }));
            toast.success('Coordinate rilevate · completa i campi manualmente');
          }
        } catch (e) {
          // Fallback: imposta sempre le coordinate GPS; messaggio diverso se è rate-limit
          setForm(f => ({ ...f, location: { ...f.location, lat, lng } }));
          const status = e?.response?.status;
          if (status === 503 || status === 504) {
            toast.info('Servizio di geolocalizzazione occupato · coordinate salvate, inserisci l\'indirizzo manualmente');
          } else {
            toast.success('Coordinate rilevate · completa i campi manualmente');
          }
        } finally {
          setGeoLocating(false);
        }
      },
      (err) => {
        setGeoLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error('Permesso negato · abilita la geolocalizzazione nelle impostazioni');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          toast.error('Posizione non disponibile · verifica il GPS');
        } else if (err.code === err.TIMEOUT) {
          toast.error('Timeout nel rilevare la posizione · riprova');
        } else {
          toast.error('Impossibile rilevare la posizione');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Pubblica un terreno</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Wizard guidato · 6 passi.</h1>

      {/* Progress */}
      <div className="mt-10 grid grid-cols-6 gap-2">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-bold border ${i<=step?'bg-foreground text-background border-foreground':'border-border text-muted-foreground'}`}>
              {i<step ? <Check size={12}/> : i+1}
            </div>
            <div className="hidden md:block text-xs text-muted-foreground truncate">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl border border-border p-8 bg-card" data-testid={PUBLISH.step(step+1)}>
        {step===0 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl">Tipo di annuncio</h2>
            <div className="grid grid-cols-2 gap-3">
              {['sale','rent'].map(t => (
                <button key={t} onClick={()=>setF('listing_type', t)}
                  className={`rounded-xl border p-6 text-left ${form.listing_type===t?'border-foreground bg-secondary':'border-border'}`}>
                  <div className="font-display text-xl">{t==='sale'?'Vendita':'Affitto'}</div>
                  <div className="text-sm text-muted-foreground mt-1">{t==='sale'?'Cedi la proprietà del terreno.':'Concedi il terreno in affitto.'}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step===1 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl">Informazioni principali</h2>
            <div><Label>Titolo *</Label><Input data-testid="publish-title-input" value={form.title} onChange={e=>setF('title', e.target.value)} placeholder="Es. Terreno agricolo di 10 ha in Toscana"/></div>
          <div><Label>Descrizione</Label><Textarea data-testid="publish-description-input" rows={5} value={form.description} onChange={e=>setF('description', e.target.value)}/>
            <button type="button" onClick={async ()=>{
              if (!form.title || !form.area_hectares) { toast('Compila prima titolo e superficie'); return; }
              try {
                const { data } = await api.post('/ai/improve-description', {
                  title: form.title, listing_type: form.listing_type,
                  area_hectares: Number(form.area_hectares), price: form.price ? Number(form.price) : null,
                  region: form.location.region, comune: form.location.comune,
                  features: form.features, crops: form.crops, hint: form.description,
                });
                setF('description', data.description); toast.success('Descrizione generata');
              } catch (e) { toast.error(formatApiError(e)); }
            }} data-testid="ai-generate-description-btn" className="mt-2 text-xs text-terracotta hover:underline inline-flex items-center gap-1">
              ✨ Genera descrizione con AI
            </button>
          </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Superficie (ha) *</Label><Input data-testid="publish-area-input" type="number" step="0.1" value={form.area_hectares} onChange={e=>setF('area_hectares', e.target.value)}/></div>
              <div>
                <Label>{form.listing_type === 'rent' ? 'Canone (€)' : 'Prezzo (€)'}</Label>
                <Input type="number" value={form.price} onChange={e=>setF('price', e.target.value)}/>
              </div>
            </div>
            {form.listing_type === 'rent' && (
              <div>
                <Label>Periodicità canone</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[['monthly','al mese','€/mese'],['yearly','all\'anno','€/anno']].map(([v,l,sub])=>(
                    <button
                      key={v}
                      type="button"
                      onClick={()=>setF('rental_period', v)}
                      data-testid={`publish-rental-period-${v}`}
                      className={`rounded-xl border p-3 text-left transition-colors ${form.rental_period===v ? 'border-foreground bg-secondary' : 'border-border hover:bg-secondary/50'}`}
                    >
                      <div className="text-sm font-medium">Canone {l}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Visualizzato come "{sub}"</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-4 mt-2 border-t border-border/60 space-y-3">
              <div>
                <Label>Canali di contatto rapido <span className="text-muted-foreground text-xs">(consigliato — le conversioni triplicano)</span></Label>
                <p className="text-xs text-muted-foreground mt-1">Compaiono sull'annuncio come bottoni WhatsApp e Chiama. Usa il formato internazionale (+39...) o solo il numero italiano.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Telefono</Label>
                  <Input data-testid="publish-phone-input" placeholder="+39 333 1234567" value={form.contact_phone} onChange={e=>setF('contact_phone', e.target.value)}/>
                </div>
                <div>
                  <Label className="text-xs">WhatsApp</Label>
                  <Input data-testid="publish-whatsapp-input" placeholder="+39 333 1234567" value={form.contact_whatsapp} onChange={e=>setF('contact_whatsapp', e.target.value)}/>
                </div>
              </div>
            </div>
          </div>
        )}
        {step===2 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl">Localizzazione</h2>
            <div className="rounded-xl border border-border bg-secondary/40 p-4 space-y-3">
              <Label className="text-xs uppercase tracking-widest">Trova posizione tramite indirizzo</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="publish-address-input"
                  value={addressQuery}
                  onChange={e=>setAddressQuery(e.target.value)}
                  onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); runGeocode(); } }}
                  placeholder="Es. Via Roma 12, Montepulciano — oppure semplicemente Chianti, Siena"
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={runGeocode}
                  disabled={geocoding}
                  data-testid="publish-geocode-btn"
                  className="rounded-full shrink-0"
                >
                  {geocoding ? 'Ricerca…' : 'Trova sulla mappa'}
                </Button>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <div className="h-px flex-1 bg-border"/>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">oppure</span>
                <div className="h-px flex-1 bg-border"/>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={useCurrentPosition}
                disabled={geoLocating}
                data-testid="publish-use-current-position-btn"
                className="w-full rounded-full border-chartreuse/40 text-chartreuse hover:bg-chartreuse/10 hover:text-chartreuse"
              >
                <Crosshair size={14} className={`mr-2 ${geoLocating ? 'animate-pulse' : ''}`}/>
                {geoLocating ? 'Rilevo la tua posizione…' : 'Usa la mia posizione attuale'}
              </Button>
              {geoResults.length > 1 && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-1">Altri risultati:</div>
                  {geoResults.slice(1).map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={()=>applyGeoResult(r)}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-background border border-transparent hover:border-border"
                    >
                      {r.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Indirizzo</Label><Input value={form.location.address} onChange={e=>setLoc('address', e.target.value)} placeholder="Via / località"/></div>
              <div><Label>Comune</Label><Input value={form.location.comune} onChange={e=>setLoc('comune', e.target.value)}/></div>
              <div><Label>Provincia</Label><Input value={form.location.province} onChange={e=>setLoc('province', e.target.value)}/></div>
              <div><Label>Regione</Label><Input value={form.location.region} onChange={e=>setLoc('region', e.target.value)}/></div>
            </div>
            <div className="text-sm text-muted-foreground">Puoi affinare la posizione cliccando direttamente sulla mappa.</div>
            <MapView height="380px" center={[form.location.lat, form.location.lng]} zoom={form.location.lat!==42.5?13:6}
              pickedPoint={{lat: form.location.lat, lng: form.location.lng}}
              onPick={(ll) => setForm(f => ({ ...f, location: { ...f.location, lat: ll.lat, lng: ll.lng } }))} />
            <div className="text-xs text-muted-foreground">Lat: {form.location.lat.toFixed(4)} · Lng: {form.location.lng.toFixed(4)}</div>
          </div>
        )}
        {step===3 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl">Caratteristiche</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FEATS.map(([k,l]) => (
                <label key={k} className={`rounded-xl border p-3 text-sm flex items-center gap-2 cursor-pointer ${form.features[k]?'border-foreground bg-secondary':'border-border'}`}>
                  <Checkbox checked={!!form.features[k]} onCheckedChange={()=>toggleFeat(k)}/>
                  {l}
                </label>
              ))}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Colture compatibili</Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {CROPS.map(c => (
                  <button key={c} type="button" onClick={()=>toggleCrop(c)}
                          className={`text-xs px-3 py-1.5 rounded-full border ${form.crops.includes(c)?'bg-foreground text-background border-foreground':'border-border'}`}>{c}</button>
                ))}
              </div>
            </div>
          </div>
        )}
        {step===4 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl">Foto</h2>
            <label className="rounded-2xl border-2 border-dashed border-border p-10 grid place-items-center cursor-pointer hover:bg-secondary/40">
              <Upload size={22}/><div className="mt-3 text-sm">Trascina o clicca per caricare (max 8MB, JPG/PNG/WEBP)</div>
              <input type="file" className="hidden" accept="image/*" multiple onChange={e=>upload(Array.from(e.target.files||[]))}/>
            </label>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {form.images.map(id => (
                  <div key={id} className="relative">
                    <img src={imgUrl(id)} alt="" className="aspect-square w-full object-cover rounded-xl border border-border"/>
                    <button onClick={()=>setForm(f=>({...f, images: f.images.filter(x=>x!==id)}))}
                      className="absolute top-1 right-1 rounded-full bg-background border border-border p-1"><X size={12}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {step===5 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl">Riepilogo</h2>
            <ul className="text-sm space-y-2">
              <li><b>Tipo:</b> {form.listing_type==='sale'?'Vendita':'Affitto'}</li>
              <li><b>Titolo:</b> {form.title || <span className="text-destructive">mancante</span>}</li>
              <li><b>Superficie:</b> {form.area_hectares || '—'} ha</li>
              <li>
                <b>{form.listing_type === 'rent' ? 'Canone' : 'Prezzo'}:</b>{' '}
                {form.price
                  ? `€ ${form.price}${form.listing_type === 'rent' ? (form.rental_period === 'monthly' ? '/mese' : '/anno') : ''}`
                  : 'Su richiesta'}
              </li>
              <li><b>Località:</b> {[form.location.address, form.location.comune, form.location.region].filter(Boolean).join(', ') || '—'}</li>
              <li><b>Coordinate:</b> {form.location.lat.toFixed(4)}, {form.location.lng.toFixed(4)}</li>
              <li><b>Foto:</b> {form.images.length}</li>
              <li><b>Colture:</b> {form.crops.join(', ') || '—'}</li>
            </ul>
            <div className="rounded-xl bg-secondary p-4 text-sm">L'annuncio sarà <b>pubblicato immediatamente</b> e visibile a tutti gli utenti. Potrai modificarlo o rimuoverlo in qualsiasi momento dalla tua dashboard.</div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="ghost" onClick={prev} disabled={step===0} data-testid={PUBLISH.prev}><ArrowLeft size={14} className="mr-2"/> Indietro</Button>
        {step < STEPS.length-1 ? (
          <Button onClick={next} data-testid={PUBLISH.next} className="rounded-full">Avanti <ArrowRight size={14} className="ml-2"/></Button>
        ) : (
          <Button onClick={submit} disabled={busy || !form.title || !form.area_hectares} data-testid={PUBLISH.submit} className="rounded-full">
            {busy ? 'Invio…' : 'Pubblica annuncio'}
          </Button>
        )}
      </div>
      <PublishSuccessModal
        open={!!success}
        onClose={() => { setSuccess(null); nav('/dashboard'); }}
        title="Annuncio pubblicato!"
        subtitle={success?.title ? `"${success.title}" è già visibile a tutti su AgriNexus.` : null}
        primaryCta={success ? { label: 'Vedi l\'annuncio', to: `/terreno/${success.listing_id}` } : null}
        secondaryCta={{ label: 'Vai alla dashboard', to: '/dashboard' }}
      />
    </div>
  );
}
